import { CheckCircle, Download, FileText, Info, Loader2, X, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { trpc } from "~/api/trpc";
import { Language, useLanguageStore } from "~/stores/language-store";

type ConfirmState = 'idle' | 'confirming' | 'polling' | 'processing';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60_000;

const formatEur = (cents: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const formatDate = (value: string | Date, isGerman: boolean) =>
    new Date(value).toLocaleDateString(isGerman ? 'de-DE' : 'en-GB', { timeZone: 'Europe/Berlin' });

const INVOICE_TYPE_LABELS: Record<string, { de: string; en: string }> = {
    GOA_INVOICE: { de: 'Rechnung', en: 'Invoice' },
    GENERAL_INVOICE: { de: 'Rechnung', en: 'Invoice' },
    BUNDLE_INVOICE: { de: 'Rechnung', en: 'Invoice' },
    GIFT_CARD_INVOICE: { de: 'Rechnung', en: 'Invoice' },
    AUSFALL_INVOICE: { de: 'Zahlungsaufforderung Ausfallentschädigung', en: 'Payment request: cancellation compensation' },
    GOA_REFUND_INVOICE: { de: 'Gutschrift', en: 'Credit note' },
    GENERAL_REFUND_INVOICE: { de: 'Gutschrift', en: 'Credit note' },
    BUNDLE_REFUND_INVOICE: { de: 'Gutschrift', en: 'Credit note' },
    GIFT_CARD_REFUND_INVOICE: { de: 'Gutschrift', en: 'Credit note' },
    AUSFALL_REFUND_INVOICE: { de: 'Gutschrift Ausfallentschädigung', en: 'Credit note: cancellation compensation' },
};

/**
 * Keeps the token out of Referer headers and the page out of search indexes while mounted.
 */
const usePrivatePageMeta = () => {
    useEffect(() => {
        const added = [
            { name: 'robots', content: 'noindex, nofollow' },
            { name: 'referrer', content: 'no-referrer' },
        ].map(({ name, content }) => {
            const meta = document.createElement('meta');
            meta.name = name;
            meta.content = content;
            document.head.appendChild(meta);
            return meta;
        });
        return () => added.forEach(meta => meta.remove());
    }, []);
};

const BillingDraftPaymentPage = () => {
    usePrivatePageMeta();

    const { token = '' } = useParams<{ token: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { language } = useLanguageStore();
    const isGerman = language === Language.GERMAN;
    const t = (de: string, en: string) => (isGerman ? de : en);

    const [confirmState, setConfirmState] = useState<ConfirmState>(
        searchParams.get('checkout_session_id') ? 'confirming' : 'idle'
    );
    const [showCancelledBanner, setShowCancelledBanner] = useState(searchParams.get('checkout') === 'cancelled');
    const [actionError, setActionError] = useState<string | null>(null);
    const confirmStartedRef = useRef(false);

    const pageQuery = trpc.publicBillingDraftPaymentPage.getPage.useQuery(
        { token },
        {
            enabled: !!token,
            retry: false,
            refetchOnWindowFocus: false,
            refetchInterval: confirmState === 'polling' ? POLL_INTERVAL_MS : false,
        },
    );
    const confirmMutation = trpc.publicBillingDraftPaymentPage.confirmCheckout.useMutation();
    const checkoutMutation = trpc.publicBillingDraftPaymentPage.createCheckout.useMutation();
    const downloadMutation = trpc.publicBillingDraftPaymentPage.getInvoiceDownloadUrl.useMutation();

    const page = pageQuery.data;

    // Returning from Stripe: record the payment, then wait until the system reports the draft as paid.
    useEffect(() => {
        const sessionId = searchParams.get('checkout_session_id');
        if (searchParams.get('checkout') === 'cancelled') {
            navigate(`/pay/${token}`, { replace: true });
        }
        if (!sessionId || confirmStartedRef.current) return;
        confirmStartedRef.current = true;

        confirmMutation.mutate(
            { token, sessionId },
            {
                onSettled: async () => {
                    navigate(`/pay/${token}`, { replace: true });
                    const result = await pageQuery.refetch();
                    setConfirmState(result.data?.isPaid ? 'idle' : 'polling');
                },
            },
        );
    }, []);

    useEffect(() => {
        if (confirmState !== 'polling') return;
        if (page?.isPaid) {
            setConfirmState('idle');
            return;
        }
        const timeout = setTimeout(() => setConfirmState('processing'), POLL_TIMEOUT_MS);
        return () => clearTimeout(timeout);
    }, [confirmState, page?.isPaid]);

    const startCheckout = () => {
        setActionError(null);
        setShowCancelledBanner(false);
        checkoutMutation.mutate(
            { token },
            {
                onSuccess: ({ url }) => window.location.assign(url),
                onError: (err) => setActionError(err.message || t('Ein Fehler ist aufgetreten.', 'An error occurred.')),
            },
        );
    };

    const downloadInvoice = (invoiceId: string) => {
        setActionError(null);
        downloadMutation.mutate(
            { token, invoiceId },
            {
                onSuccess: ({ url }) => window.location.assign(url),
                onError: (err) => setActionError(err.message || t('Download fehlgeschlagen.', 'Download failed.')),
            },
        );
    };

    if (pageQuery.isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <Loader2 className="w-10 h-10 text-burgundy animate-spin" />
            </div>
        );
    }

    if (pageQuery.isError || !page) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center flex flex-col items-center gap-4">
                    <XCircle className="w-12 h-12 text-burgundy/60" />
                    <h2 className="text-xl font-medium text-burgundy">
                        {t('Link ungültig oder abgelaufen', 'Link invalid or expired')}
                    </h2>
                    <p className="text-sm text-burgundy/60 font-light leading-relaxed">
                        {t(
                            'Dieser Link ist nicht mehr gültig. Bitte wende dich an Kalia Lab, wenn du einen neuen Link benötigst.',
                            'This link is no longer valid. Please contact Kalia Lab if you need a new link.',
                        )}
                    </p>
                </div>
            </div>
        );
    }

    const isConfirming = confirmState === 'confirming' || confirmState === 'polling';
    const canPay = !page.isPaid && confirmState === 'idle' && page.totals.remainingCents > 0;

    return (
        <div className="min-h-screen px-4 py-10 md:py-16 flex justify-center">
            <div className="max-w-2xl w-full flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-medium text-burgundy">
                        {t(`Hallo ${page.customerFirstName},`, `Hi ${page.customerFirstName},`)}
                    </h1>
                    <p className="text-sm text-burgundy/70 font-light leading-relaxed">
                        {page.isPaid
                            ? t('hier findest du die Übersicht zu deinen offenen Zahlungen.', 'here is the overview of your open payments.')
                            : t('hier findest du die Übersicht zu deinen offenen Zahlungen. Du kannst den offenen Betrag direkt online bezahlen.', 'here is the overview of your open payments. You can pay the open amount online right away.')}
                    </p>
                </div>

                {page.isPaid && (
                    <div className="flex items-start gap-3 rounded-xl bg-green-50 border border-green-200 p-4">
                        <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-green-800">{t('Bezahlt', 'Paid')}</span>
                            <span className="text-sm text-green-700 font-light">
                                {t('Vielen Dank! Deine Rechnungen sind vollständig bezahlt.', 'Thank you! Your invoices have been paid in full.')}
                            </span>
                        </div>
                    </div>
                )}

                {showCancelledBanner && !page.isPaid && (
                    <div className="flex items-start gap-3 rounded-xl bg-white border border-burgundy/20 p-4">
                        <Info className="w-5 h-5 text-burgundy shrink-0 mt-0.5" />
                        <span className="text-sm text-burgundy/80 font-light flex-1">
                            {t('Die Zahlung wurde abgebrochen. Du kannst es jederzeit erneut versuchen.', 'The payment was cancelled. You can try again at any time.')}
                        </span>
                        <button onClick={() => setShowCancelledBanner(false)} aria-label={t('Schließen', 'Close')}>
                            <X className="w-4 h-4 text-burgundy/60" />
                        </button>
                    </div>
                )}

                {confirmState === 'processing' && !page.isPaid && (
                    <div className="flex items-start gap-3 rounded-xl bg-white border border-burgundy/20 p-4">
                        <Info className="w-5 h-5 text-burgundy shrink-0 mt-0.5" />
                        <span className="text-sm text-burgundy/80 font-light">
                            {t(
                                'Deine Zahlung wird noch verarbeitet. Das kann einen Moment dauern – lade die Seite später erneut, um den aktuellen Stand zu sehen.',
                                'Your payment is still being processed. This can take a moment – reload this page later to see the current status.',
                            )}
                        </span>
                    </div>
                )}

                <div className="rounded-xl bg-white p-5 md:p-6 flex flex-col gap-4">
                    <div className="flex flex-wrap justify-between gap-2 text-sm">
                        <span className="font-medium text-burgundy">
                            {t('Übersicht', 'Overview')}
                        </span>
                        <span className="text-burgundy/60 font-light">
                            {!page.isPaid && ` · ${t('fällig am', 'due')} ${formatDate(page.dueDate, isGerman)}`}
                        </span>
                    </div>

                    <div className="flex flex-col divide-y divide-burgundy/10">
                        {page.lineItems.map(item => (
                            <div key={item.id} className="flex justify-between gap-4 py-3 text-sm">
                                <span className="text-burgundy/80 font-light leading-snug">{item.description}</span>
                                <span className="text-burgundy whitespace-nowrap">{formatEur(item.totalGrossCents)}</span>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col gap-2 border-t border-burgundy/20 pt-4 text-sm">
                        <div className="flex justify-between text-burgundy/60 font-light">
                            <span>{t('Netto', 'Net')}</span>
                            <span>{formatEur(page.totals.totalNetCents)}</span>
                        </div>
                        <div className="flex justify-between text-burgundy/60 font-light">
                            <span>{t('MwSt.', 'VAT')}</span>
                            <span>{formatEur(page.totals.vatCents)}</span>
                        </div>
                        <div className="flex justify-between font-medium text-burgundy">
                            <span>{t('Gesamt', 'Total')}</span>
                            <span>{formatEur(page.totals.totalGrossCents)}</span>
                        </div>
                        {page.totals.paidCents !== 0 && (
                            <div className="flex justify-between text-burgundy/60 font-light">
                                <span>{t('Bereits bezahlt', 'Already paid')}</span>
                                <span>{formatEur(page.totals.paidCents)}</span>
                            </div>
                        )}
                        {!page.isPaid && (
                            <div className="flex justify-between text-base font-medium text-burgundy pt-1">
                                <span>{t('Offener Betrag', 'Amount due')}</span>
                                <span>{formatEur(page.totals.remainingCents)}</span>
                            </div>
                        )}
                    </div>

                    {isConfirming && (
                        <div className="flex items-center justify-center gap-3 rounded-lg bg-light-burgundy py-4 text-sm text-burgundy">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('Deine Zahlung wird bestätigt...', 'Confirming your payment...')}
                        </div>
                    )}

                    {canPay && (
                        <button
                            onClick={startCheckout}
                            disabled={checkoutMutation.isPending}
                            className="w-full px-6 py-3 bg-burgundy text-white rounded-lg text-sm font-medium hover:bg-burgundy/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                            {checkoutMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                            {t(`Jetzt ${formatEur(page.totals.remainingCents)} bezahlen`, `Pay ${formatEur(page.totals.remainingCents)} now`)}
                        </button>
                    )}
                </div>

                {page.invoices.length > 0 && (
                    <div className="rounded-xl bg-white p-5 md:p-6 flex flex-col gap-3">
                        <h2 className="text-sm font-medium text-burgundy">{t('Rechnungen zum Download', 'Invoices for download')}</h2>
                        {page.invoices.map(invoice => {
                            const label = INVOICE_TYPE_LABELS[invoice.type] ?? { de: 'Rechnung', en: 'Invoice' };
                            const isDownloading = downloadMutation.isPending && downloadMutation.variables?.invoiceId === invoice.id;
                            return (
                                <button
                                    key={invoice.id}
                                    onClick={() => downloadInvoice(invoice.id)}
                                    disabled={isDownloading}
                                    className="flex items-center gap-3 rounded-lg border border-burgundy/15 px-4 py-3 text-left hover:bg-light-burgundy transition-colors disabled:opacity-60"
                                >
                                    <FileText className="w-5 h-5 text-burgundy/70 shrink-0" />
                                    <div className="flex flex-col gap-1 flex-1">
                                        <span className="text-sm text-burgundy">{isGerman ? label.de : label.en} {invoice.invoiceNumber}</span>
                                        <span className="text-xs text-burgundy/60 font-light">{formatDate(invoice.invoiceDate, isGerman)}</span>
                                    </div>
                                    {isDownloading
                                        ? <Loader2 className="w-4 h-4 text-burgundy animate-spin" />
                                        : <Download className="w-4 h-4 text-burgundy/70" />}
                                </button>
                            );
                        })}
                    </div>
                )}

                {actionError && (
                    <p className="text-sm text-red-600 font-light text-center">{actionError}</p>
                )}

                <p className="text-xs text-burgundy/50 font-light text-center leading-relaxed">
                    {page.company.name} · {page.company.address} · {page.company.city}
                </p>
            </div>
        </div>
    );
};

export default BillingDraftPaymentPage;
